import React, { createContext, useContext, useState, useEffect } from 'react';
import DiscordService from '../services/DiscordService';
import { useToast } from '@chakra-ui/react';

// Create Discord context
const DiscordContext = createContext();

export const useDiscord = () => useContext(DiscordContext);

export const DiscordProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isGuildMember, setIsGuildMember] = useState(false);
  const [user, setUser] = useState(null);
  const [guildMember, setGuildMember] = useState(null);
  const [channels, setChannels] = useState([]);
  const [palmHeistChannels, setPalmHeistChannels] = useState({
    all: [],
    general: null,
    announcements: null,
    trading: null,
    help: null
  });
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  // Initialize Discord connection
  useEffect(() => {
    const checkConnection = async () => {
      if (DiscordService.isConnected()) {
        setIsConnected(true);
        setUser(DiscordService.user);
        setIsGuildMember(DiscordService.isGuildMember());
        setGuildMember(DiscordService.guildMember);
        await fetchPalmHeistChannels();
      }
    };

    checkConnection();
  }, []);

  // Fetch Palm Heist specific channels
  const fetchPalmHeistChannels = async () => {
    setLoading(true);
    try {
      // Always use hardcoded mock channels that match the actual server
      const mockChannels = [
        { id: '1', name: 'verify', type: 0, position: 0 },
        { id: '2', name: 'rules', type: 0, position: 1 },
        { id: '3', name: 'help', type: 0, position: 2 },
        { id: '4', name: 'guild-xyz', type: 0, position: 3 },
        { id: '5', name: 'official-links', type: 0, position: 4 },
        { id: '6', name: 'announcements', type: 0, position: 5 },
        { id: '7', name: 'smart-contracts', type: 0, position: 6 },
        { id: '8', name: 'chat', type: 0, position: 7 },
        { id: '9', name: 'og-chat', type: 0, position: 8 },
        { id: '10', name: 'travel-guide', type: 0, position: 9 },
        { id: '11', name: 'ask-questions', type: 0, position: 10 },
        { id: '12', name: 'contact-us', type: 0, position: 11 },
        { id: '13', name: 'wl-evm', type: 0, position: 12 },
        { id: '14', name: 'feed', type: 0, position: 13 }
      ];
      
      const voiceChannels = [
        { id: '15', name: 'voice-general', type: 2, position: 0 }
      ];
      
      // Create a reliable data structure
      const channelsData = {
        all: mockChannels,
        voice: voiceChannels,
        chat: mockChannels.find(c => c.name === 'chat'),
        announcements: mockChannels.find(c => c.name === 'announcements'),
        help: mockChannels.find(c => c.name === 'help'),
        guildInfo: {
          id: '1037517581063553104',
          name: 'PalmHeist Discord',
          icon: null
        }
      };
      
      console.log('Using hardcoded channels:', channelsData);
      setPalmHeistChannels(channelsData);
      setChannels(channelsData.all);
      
      // Set chat channel as active by default
      const chatChannel = mockChannels.find(c => c.name === 'chat');
      console.log('Setting chat channel as default:', chatChannel);
      
      if (chatChannel) {
        setActiveChannel(chatChannel);
        // Mock messages for the chat channel
        setMessages([
          {
            id: '1',
            content: 'Welcome to the Palm Heist Discord chat!',
            author: {
              username: 'PalmHeist Bot',
              bot: true
            },
            timestamp: new Date().toISOString()
          }
        ]);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to set up Discord channels');
      console.error(err);
      toast({
        title: "Discord Error",
        description: "Failed to set up Discord channels. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch all channels
  const fetchChannels = async () => {
    setLoading(true);
    try {
      const channelsData = await DiscordService.fetchChannels();
      setChannels(channelsData);
      
      // Set first text channel as active by default
      const textChannels = channelsData.filter(channel => channel.type === 0);
      if (textChannels.length > 0 && !activeChannel) {
        setActiveChannel(textChannels[0]);
        // Set mock messages for the default channel
        setMessages([
          {
            id: textChannels[0].id + '-1',
            content: `Welcome to the #${textChannels[0].name} channel!`,
            author: {
              username: 'PalmHeist Bot',
              bot: true
            },
            timestamp: new Date().toISOString()
          }
        ]);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to fetch Discord channels');
      console.error(err);
      toast({
        title: "Discord Error",
        description: "Failed to fetch Discord channels. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Change active channel
  const changeChannel = async (channel) => {
    if (!channel || (activeChannel && channel.id === activeChannel.id)) return;
    
    console.log('Changing to channel:', channel.name);
    setActiveChannel(channel);
    
    // Set mock messages for the channel
    setMessages([
      {
        id: channel.id + '-1',
        content: `Welcome to the #${channel.name} channel!`,
        author: {
          username: 'PalmHeist Bot',
          bot: true
        },
        timestamp: new Date().toISOString()
      }
    ]);
  };
  
  // Send a message to the active channel
  const sendMessage = async (content) => {
    if (!activeChannel || !content.trim()) return;
    
    setLoading(true);
    try {
      // Simulate sending a message
      console.log('Sending message to channel:', activeChannel.id, content);
      
      // Add the message to the local state
      const newMessage = {
        id: Date.now().toString(),
        content: content,
        author: {
          id: '123456789',
          username: 'You',
          discriminator: '1234',
          avatar: null
        },
        timestamp: new Date().toISOString()
      };
      
      setMessages(prevMessages => [...prevMessages, newMessage]);
      setError(null);
      
      // Show success toast
      toast({
        title: "Message Sent",
        description: "Your message has been sent.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      setError('Failed to send message');
      console.error(err);
      toast({
        title: "Discord Error",
        description: "Failed to send message. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Connect to Discord
  const connect = async () => {
    // For now, just simulate a successful connection
    console.log('Simulating Discord connection');
    setIsConnected(true);
    setUser({
      id: '123456789',
      username: 'PalmHeist_User',
      discriminator: '1234',
      avatar: null
    });
    
    // Fetch channels after connection
    await fetchPalmHeistChannels();
    
    // Show success toast
    toast({
      title: "Discord Connected",
      description: "Successfully connected to Discord.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    
    // Uncomment this for real OAuth flow
    // window.location.href = DiscordService.getAuthUrl();
  };

  // Handle OAuth callback
  const handleCallback = async (code) => {
    setLoading(true);
    try {
      // Simulate a successful callback
      console.log('Simulating OAuth callback');
      setIsConnected(true);
      setUser({
        id: '123456789',
        username: 'PalmHeist_User',
        discriminator: '1234',
        avatar: null
      });
      
      // Fetch channels after callback
      await fetchPalmHeistChannels();
      
      // Show success toast
      toast({
        title: "Discord Connected",
        description: "Successfully connected to Discord.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      const success = await DiscordService.getToken(code);
      if (success) {
        setIsConnected(true);
        setUser(DiscordService.user);
        setIsGuildMember(DiscordService.isGuildMember());
        setGuildMember(DiscordService.guildMember);
        await fetchPalmHeistChannels();
        
        toast({
          title: "Discord Connected",
          description: "Successfully connected to Discord!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        
        return true;
      } else {
        setError('Failed to authenticate with Discord');
        toast({
          title: "Discord Error",
          description: "Failed to authenticate with Discord. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return false;
      }
    } catch (err) {
      setError('Error during Discord authentication');
      console.error(err);
      toast({
        title: "Discord Error",
        description: "An error occurred during Discord authentication. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Disconnect from Discord
  const disconnect = () => {
    DiscordService.logout();
    setIsConnected(false);
    setIsGuildMember(false);
    setUser(null);
    setGuildMember(null);
    setChannels([]);
    setPalmHeistChannels({
      all: [],
      general: null,
      announcements: null,
      trading: null,
      help: null
    });
    setActiveChannel(null);
    setMessages([]);
    
    toast({
      title: "Disconnected",
      description: "You have been disconnected from Discord.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  // Join the Palm Heist server
  const joinPalmHeistServer = async () => {
    setLoading(true);
    try {
      const success = await DiscordService.joinGuild();
      if (success) {
        await DiscordService.fetchGuildMember();
        setIsGuildMember(true);
        setGuildMember(DiscordService.guildMember);
        
        toast({
          title: "Server Joined",
          description: "Successfully joined the Palm Heist Discord server!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        
        return true;
      } else {
        toast({
          title: "Discord Error",
          description: "Failed to join the Palm Heist server. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return false;
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "Discord Error",
        description: "An error occurred while joining the server. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Refresh messages in the active channel
  const refreshMessages = async () => {
    if (activeChannel) {
      // Add a new system message
      const refreshMessage = {
        id: Date.now().toString(),
        content: 'Messages refreshed',
        author: {
          username: 'PalmHeist Bot',
          bot: true
        },
        timestamp: new Date().toISOString()
      };
      setMessages(prevMessages => [...prevMessages, refreshMessage]);
    }
  };

  // Set up polling for regular message updates
  useEffect(() => {
    if (!activeChannel) return;
    
    const intervalId = setInterval(() => {
      refreshMessages();
    }, 10000); // Refresh every 10 seconds
    
    return () => clearInterval(intervalId);
  }, [activeChannel]);

  // Define a simple sendMessage function if it doesn't exist
  const handleSendMessage = async (content) => {
    if (!activeChannel || !content.trim()) return;
    
    setLoading(true);
    try {
      // Simulate sending a message
      console.log('Sending message to channel:', activeChannel.id, content);
      
      // Add the message to the local state
      const newMessage = {
        id: Date.now().toString(),
        content: content,
        author: {
          id: '123456789',
          username: 'You',
          discriminator: '1234',
          avatar: null
        },
        timestamp: new Date().toISOString()
      };
      
      setMessages(prevMessages => [...prevMessages, newMessage]);
      setError(null);
      
      // Show success toast
      toast({
        title: "Message Sent",
        description: "Your message has been sent.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      setError('Failed to send message');
      console.error(err);
      toast({
        title: "Discord Error",
        description: "Failed to send message. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DiscordContext.Provider
      value={{
        isConnected,
        isGuildMember,
        user,
        guildMember,
        channels,
        palmHeistChannels,
        activeChannel,
        messages,
        loading,
        error,
        connect,
        disconnect,
        handleCallback,
        changeChannel,
        sendMessage: handleSendMessage,
        fetchChannels,
        fetchPalmHeistChannels,
        refreshMessages
      }}
    >
      {children}
    </DiscordContext.Provider>
  );
};
